'use client';

import type { Map as LeafletMap } from 'leaflet';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/button';
import Drawer from '@/components/drawer';
import CustomFiltersIcon from '@/components/icons/customFiltersIcon';
import CustomMapPinIcon from '@/components/icons/customMapPinIcon';
import { LocationSearchInput } from '@/components/locationSearchInput';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { CircularProgress } from '@/components/ui/circularProgress';
import { IconButton } from '@/components/ui/iconButton';
import { CloseIcon } from '@/components/ui/icons';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader
} from '@/components/ui/list';
import { COMPANY_LOCATION } from '@/config/static';
import { MapMoveEndEventProps } from '@/features/maps/map';
import { GeoPoints } from '@/features/maps/types';
import { useNotificationsDispatch } from '@/features/notifications/notificationsContext';
import { placesFetch, placesFetchMore } from '@/features/places/state/actions';
import {
  usePlacesDispatch,
  usePlacesIsLoading,
  usePlacesItems,
  usePlacesNextRangeCount
} from '@/features/places/state/context';
import { PlaceDTO } from '@/features/places/types/places.dto';
import { ProductPriceFilter } from '@/features/products/components/list/filters/priceFilter/productPriceFilter';
import { ProductFiltersAccordion } from '@/features/products/components/list/filters/productFiltersAccordion';
import ProductLocationCard from '@/features/products/components/location/productLocationCard';
import { dispatchGeoNotification } from '@/features/products/components/location/productLocationNotifications';
import { ProductDrawerAvatar } from '@/features/products/components/productDrawerAvatar';
import { ProductDrawerBackBtn } from '@/features/products/components/productDrawerBackBtn';
import { ProductFilterBtnGroup } from '@/features/products/components/productFilterBtnGroup';
import { hasProductLocation, showProductName } from '@/features/products/data/utils';
import {
  productListFetchByLocation,
  productListUpdateFilters
} from '@/features/products/state/list/actions';
import {
  useProductListDispatch,
  useProductListFilters,
  useProductListItems,
  useProductListMapItems,
  useProductListSelectedFilters,
  useProductListTaxonData
} from '@/features/products/state/list/context';
import { ProductListingItem } from '@/features/products/types/product';
import { ProductNameType } from '@/features/products/types/productNames';
import { replacePlaceholders, translate } from '@/i18n';
import { listingTranslate } from '@/i18n/translations/listingTranslations';
import { decimalPrecision, formatNumberToCurrency } from '@/lib/currencyFormat';
import { getDevicePosition } from '@/lib/getDevicePosition';

interface ProductsListLocationProps {
  openButtonText: string;
  closeButtonText: string;
  showCityChips: boolean;
  showStartIcon?: boolean;
}

export const Map = dynamic(
  () =>
    import('@/features/maps/map').catch((err) => {
      console.log(err);
      return () => <div />;
    }),
  {
    ssr: false,
    loading: () => <div className="h-75 bg-gray-100 rounded-md" />
  }
);

export const PriceMarker = dynamic(() => import('@/features/maps/priceMarker'), {
  ssr: false
});

export const MapCluster = dynamic(() => import('@/features/maps/cluster'), { ssr: false });

export const PulseMarker = dynamic(() => import('@/features/maps/pulseMarker'), {
  ssr: false
});

const ProductLocationDrawer = dynamic(
  () =>
    import('@/features/products/components/location/productLocationDrawer').then(
      (m) => m.ProductLocationDrawer
    ),
  { ssr: false }
);

export const ProductsListLocation = ({
  openButtonText,
  closeButtonText,
  showCityChips,
  showStartIcon
}: ProductsListLocationProps) => {
  const filters = useProductListFilters();
  const selectedFilters = useProductListSelectedFilters();
  const mapItems = useProductListMapItems();
  const items = useProductListItems();
  const taxonData = useProductListTaxonData();
  const dispatch = useProductListDispatch();

  const placesDispatch = usePlacesDispatch();
  const placesItems = usePlacesItems();
  const placesIsLoading = usePlacesIsLoading();
  const placesNextRangeCount = usePlacesNextRangeCount();

  const notificationDispatch = useNotificationsDispatch();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isFiltersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [canChangeMapCenter, setCanChangeMapCenter] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: COMPANY_LOCATION.lat,
    lng: COMPANY_LOCATION.lng
  });
  const [userMarkerCoordinates, setUserMarkerCoordinates] = useState<
    GeolocationCoordinates | undefined
  >(undefined);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductListingItem | null>(null);
  const cities = useMemo(
    () => filters.filter((filter) => filter.code === 'city')?.[0]?.items,
    [filters]
  );
  const selectedCities = useMemo(() => selectedFilters?.city || [], [selectedFilters]) as string[];
  const itemsWithLocation = useMemo(() => {
    return mapItems.filter(hasProductLocation);
  }, [mapItems]);

  const onCitySelect = (code: string) => {
    dispatch(
      productListUpdateFilters({
        filterCategoryCode: 'city',
        code
      })
    );
  };

  const onMapListCitySelect = (place: PlaceDTO) => {
    const { code, lat, lng, postcode, range, name } = place;
    dispatch(
      productListUpdateFilters({
        filterCategoryCode: 'city',
        code,
        itemProps: {
          code,
          name,
          count: 0,
          lat,
          lng,
          postcode,
          range
        }
      })
    );
  };

  const fetchMorePlaces = () => {
    placesDispatch(placesFetchMore(undefined));
  };

  const toggleDrawer = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const toggleFiltersDrawer = () => {
    setFiltersDrawerOpen(!isFiltersDrawerOpen);
  };

  const onSearchChange = (value: string) => {
    const trimmedValue = value.trim();
    setSearchInputValue(value);
    if (trimmedValue) {
      placesDispatch(placesFetch({ query: value }));
    }
  };

  const onGetGeoCoordinates = (cords: GeolocationCoordinates) => {
    setCanChangeMapCenter(true);
    setMapCenter({ lat: cords.latitude, lng: cords.longitude });
    setUserMarkerCoordinates(cords);
  };

  const onGeoLocationUnavailable = () => {
    dispatchGeoNotification(notificationDispatch, 'UNAVAILABLE');
  };

  const onGetGeoCoordinatesError = (error: GeolocationPositionError) => {
    dispatchGeoNotification(notificationDispatch, 'ERROR');
  };

  const onMapPositionChange = ({ center, range }: MapMoveEndEventProps) => {
    dispatch(productListFetchByLocation({ ...center, range }));
  };

  const onOutsideCenterChange = (center: GeoPoints, map: LeafletMap) => {
    if (canChangeMapCenter) {
      setCanChangeMapCenter(false);
      map.flyTo(center);
    }
  };

  const onListFilterChange = (code: string, filterCode: string) => {
    if (code === 'city' || code === 'voivodeship') {
      setUserMarkerCoordinates(undefined);
    }
    setCanChangeMapCenter(true);
  };

  const clearUserLocation = () => {
    setCanChangeMapCenter(true);
    setUserMarkerCoordinates(undefined);
  };

  const handleLocationSelect = useCallback((product: ProductListingItem) => {
    setSelectedProduct(product);
  }, []);

  // TODO remove location when KM-5304 is ready. If locations is present, location will be removed from BE response
  const memoizedMarkers = useMemo(() => {
    return itemsWithLocation.map((product) => {
      if (product.location && product.variant) {
        return (
          <PriceMarker
            key={product.id}
            id={product.id + 'id'}
            price={formatNumberToCurrency(
              product.variant?.price || 0,
              product.variant?.currencyCode || 'PLN',
              product?.localeCode || 'PL',
              decimalPrecision(product.variant.price)
            )}
            position={[Number(product.location.latitude), Number(product.location.longitude)]}
            name={showProductName(product, ProductNameType.Listing)}
            onLocationSelect={() => handleLocationSelect(product)}
          />
        );
      } else if (product.locations?.length && product.variant) {
        return product.locations.map((locationItem, index) => (
          <PriceMarker
            key={product.id + 'id' + index}
            id={product.id + 'id' + index}
            price={formatNumberToCurrency(
              product.variant?.price || 0,
              product.variant?.currencyCode || 'PLN',
              product?.localeCode || 'PL',
              decimalPrecision(product.variant.price)
            )}
            position={[Number(locationItem.latitude), Number(locationItem.longitude)]}
            name={showProductName(product, ProductNameType.Listing)}
            onLocationSelect={() => handleLocationSelect(product)}
          />
        ));
      }
      return null;
    });
  }, [itemsWithLocation, handleLocationSelect]);

  const memoizedPulseMarker = useMemo(() => {
    if (!userMarkerCoordinates) return null;
    return <PulseMarker position={mapCenter} />;
  }, [userMarkerCoordinates, mapCenter]);

  // TODO remove location when KM-5304 is ready. If locations is present, location will be removed from BE response
  useEffect(() => {
    // ? run only when user change global list filters ! then items changed -> set map position
    // ? find products with location and fly to its center
    if (itemsWithLocation.length) {
      const first = itemsWithLocation[0]!;
      const coords = first.location ?? first.locations?.[0];
      if (coords) {
        setMapCenter({
          lat: Number(coords.latitude),
          lng: Number(coords.longitude)
        });
      }
    }
  }, [JSON.stringify(items)]);

  return (
    <div className="flex flex-col">
      <div className="md:flex md:gap-2">
        <ProductFilterBtnGroup
          items={cities}
          selectedItems={selectedCities}
          callback={onCitySelect}
          startListChildren={
            <Button
              size="small"
              color="primary"
              className="min-w-max"
              onClick={toggleDrawer}
              startIcon={
                showStartIcon ? (
                  <CustomMapPinIcon
                    color="var(--mui-palette-primary-main)"
                    fill="var(--mui-palette-primary-main)"
                    className="size-6"
                  />
                ) : null
              }
            >
              {openButtonText}
            </Button>
          }
          chipsHiddenOnDesktop
          showCityChips={showCityChips}
        ></ProductFilterBtnGroup>
      </div>

      <ProductLocationDrawer isOpen={isDrawerOpen} onDrawerClose={toggleDrawer}>
        <ProductDrawerBackBtn callback={toggleDrawer} text={closeButtonText} />
        <ProductDrawerAvatar
          img={taxonData?.images?.[0]?.path || taxonData?.images?.[0]?.fallbackPath || ''}
          imgAlt={taxonData?.name || ''}
          title={`${listingTranslate('product.list.location.drawer.title')}:`}
          subtitle={taxonData?.name || ''}
        />
        <LocationSearchInput
          onChangeCallback={onSearchChange}
          onGetGeoCoordinates={onGetGeoCoordinates}
          onGeoLocationUnavailable={onGeoLocationUnavailable}
          onGetGeoCoordinatesError={onGetGeoCoordinatesError}
          onClearUserLocation={clearUserLocation}
          userCoordinates={userMarkerCoordinates}
        />
        {!userMarkerCoordinates && !!searchInputValue.length && (
          <p className="my-1 text-center text-base leading-[150%] font-normal">
            {translate('shared.or')}
          </p>
        )}

        {searchInputValue.length > 0 && (
          <>
            <Button
              variant="outlined"
              className="w-full mb-2"
              onClick={() =>
                getDevicePosition({
                  onGeoLocationUnavailable,
                  onGetGeoCoordinatesError,
                  onGetGeoCoordinates
                })
              }
            >
              {listingTranslate('locations.use.current.location')}
            </Button>

            <div className=" h-full overflow-y-auto">
              <List className="pt-0">
                {!!placesItems.length && (
                  <ListSubheader>
                    {translate('shared.scores')}: {placesItems.length}
                  </ListSubheader>
                )}

                {!placesItems.length && searchInputValue.length && !placesIsLoading && (
                  <div className="flex items-center justify-center my-4 w-full">
                    <p className="w-1/2 text-center text-xl font-bold">
                      {replacePlaceholders(listingTranslate('locations.search.empty'), {
                        searchTerm: searchInputValue
                      })}
                    </p>
                  </div>
                )}

                {!!placesItems.length && (
                  <>
                    {placesItems.map((place, idx) => (
                      <ListItem key={idx} disablePadding>
                        <ListItemButton
                          role="button"
                          onClick={() => onMapListCitySelect(place)}
                          dense
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={!!selectedFilters?.['city']?.includes(place.code)}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{
                                'aria-labelledby': `checkbox-list-label-${place.name}`
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={`checkbox-list-text-label-${place.name}`}
                            primary={
                              `${place.name}` +
                              (place.distanceKm
                                ? ` (${place.distanceKm} od ${place.distanceFrom})`
                                : '')
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}

                    {!!placesNextRangeCount && !placesIsLoading && (
                      <Button
                        variant="text"
                        className="w-full text-center capitalize"
                        onClick={fetchMorePlaces}
                      >
                        {listingTranslate('locations.show.more') + ` (${placesNextRangeCount})`}
                      </Button>
                    )}
                  </>
                )}

                {placesIsLoading && (
                  <div className="flex items-center justify-center mb-4">
                    <CircularProgress />
                  </div>
                )}
              </List>
            </div>

            <div className="mt-2">
              <Button
                variant="contained"
                className="w-full"
                onClick={() => setSearchInputValue('')}
              >
                {translate('show.on.map')}
              </Button>
            </div>
          </>
        )}

        {searchInputValue.length <= 0 && (
          <Map
            key={`map-${mapCenter.lat}-${mapCenter.lng}`}
            className="rounded-md relative"
            scrollWheelZoom={true}
            center={{
              lat: mapCenter.lat,
              lng: mapCenter.lng
            }}
            onMapMoveEnd={onMapPositionChange}
            onOutsideCenterChange={onOutsideCenterChange}
          >
            {!itemsWithLocation.length && (
              <Alert
                variant="outlined"
                severity="info"
                className="z-[1001] absolute top-12 left-2 bg-(--mui-palette-background-paper)"
              >
                {listingTranslate('locations.map.search.not.found')}
              </Alert>
            )}
            <Button
              size="small"
              variant="contained"
              color="inherit"
              className="absolute top-2 left-2 z-[1001]"
              onClick={toggleFiltersDrawer}
              startIcon={
                <CustomFiltersIcon
                  color="var(--mui-palette-primary-main)"
                  fill="var(--mui-palette-primary-main)"
                  className="size-6"
                />
              }
            >
              {listingTranslate('locations.map.filters.title')}
            </Button>
            <MapCluster
              chunkedLoading={true}
              zoomToBoundsOnClick={true}
              showCoverageOnHover={false}
            >
              {memoizedMarkers}
              {memoizedPulseMarker}
            </MapCluster>
            {selectedProduct && (
              <div className="absolute bottom-4 left-4 z-1000 bg-white rounded-lg shadow-xl">
                <ProductLocationCard
                  product={selectedProduct}
                  onClose={() => setSelectedProduct(null)}
                />
              </div>
            )}
          </Map>
        )}

        <Drawer
          open={isFiltersDrawerOpen}
          onClose={toggleFiltersDrawer}
          anchor="bottom"
          hideBackdrop
          ariaLabel={listingTranslate('locations.map.filters.title')}
          className="h-[50vh] w-full p-4 md:w-125 gap-4 rounded-t-lg"
        >
          <div className="overflow-y-auto">
            <div className="flex justify-end items-center">
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={toggleFiltersDrawer}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </div>

            <div className="flex flex-col gap-4">
              <ProductPriceFilter />
              {filters.map((filter, index) => (
                <ProductFiltersAccordion
                  key={index}
                  title={filter.title}
                  items={filter.items}
                  code={filter.code}
                  expanded={false}
                  onFilterChange={onListFilterChange}
                />
              ))}
            </div>
          </div>
        </Drawer>
      </ProductLocationDrawer>
    </div>
  );
};
